import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, exigir, ErroApi } from "../api/cliente";
import type { components } from "../api/esquema";
import { BarraFiltros, GrupoMenu, MenuFiltro, OpcaoMenu } from "../componentes/kit/BarraFiltros";
import { Botao } from "../componentes/kit/Botao";
import { CabecalhoTela } from "../componentes/kit/CabecalhoTela";
import { Campo, Selecao } from "../componentes/kit/Campo";
import { Esqueleto, Falha, Vazio } from "../componentes/kit/Estados";
import { Etiqueta, type TomEtiqueta } from "../componentes/kit/Etiqueta";
import { MenuPeriodo, type DataDoFiltro, type Periodo } from "../componentes/kit/FiltroPrazo";
import { LinhaTarefa, ListaTarefas, type TarefaResumo } from "../componentes/kit/ListaTarefas";
import { Dados, PainelLateral } from "../componentes/kit/PainelLateral";
import { Prazo } from "../componentes/kit/Prazo";
import { usePainel } from "../componentes/kit/painel";
import { useEu } from "../sessao/eu";

type Tarefa = components["schemas"]["Tarefa"];
type Situacao = "aberto" | "atrasadas" | "conferencia" | "semdata" | "concluidas";

const SITUACOES: { valor: Situacao; rotulo: string }[] = [
  { valor: "aberto", rotulo: "Em aberto" },
  { valor: "atrasadas", rotulo: "Atrasadas" },
  { valor: "conferencia", rotulo: "Aguardando conferência" },
  { valor: "semdata", rotulo: "Sem data" },
  { valor: "concluidas", rotulo: "Concluídas" },
];

/** Etiqueta do escritório → tom de significado do DESIGN.md (a cor da etiqueta no banco não manda na tela). */
function tomDe(nome: string): TomEtiqueta {
  const n = nome.toLowerCase();
  if (n.includes("protocol") || n.includes("pronto")) return "pronto";
  if (n.includes("confer") || n.includes("audi")) return "conferir";
  if (n.includes("aguard") || n.includes("cliente") || n.includes("doc")) return "cliente";
  if (n.includes("atras") || n.includes("urgent")) return "alerta";
  return "neutro";
}

function situacaoDe(t: Tarefa): { texto: string; tom: TomEtiqueta } {
  if (t.status === "em_conferencia") return { texto: "Em conferência", tom: "conferir" };
  if (t.status === "concluida") return { texto: "Concluída", tom: "neutro" };
  const e = t.etiquetas[0];
  return e ? { texto: e.nome, tom: tomDe(e.nome) } : { texto: "A fazer", tom: "neutro" };
}

function resumo(t: Tarefa): TarefaResumo {
  return {
    id: t.id, titulo: t.titulo, detalhe: t.descricao, cliente: t.cliente, numeroProcesso: t.processo_formatado,
    execucao: t.execucao, fatal: t.fatal, urgencia: t.urgencia, etiqueta: situacaoDe(t),
    responsavel: t.responsavel.nome, audiencia: t.tipo === "reuniao",
  };
}

/** E4-03 · Tarefas: a visão geral (US1, decisão de 21/10). Execução e prazo fatal em colunas; quem filtra é a API. */
export function Tarefas() {
  const { data: eu } = useEu();
  const qc = useQueryClient();
  const [digitado, setDigitado] = useState("");
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState<Situacao>("aberto");
  const [data, setData] = useState<DataDoFiltro>("fatal");
  const [periodo, setPeriodo] = useState<Periodo>("qualquer");
  const [soMinhas, setSoMinhas] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setBusca(digitado.trim()), 250);
    return () => window.clearTimeout(id);
  }, [digitado]);

  const consulta = useInfiniteQuery({
    queryKey: ["tarefas", { busca, situacao, data, periodo, soMinhas }],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => exigir(api.GET("/api/tarefas", {
      params: { query: {
        situacao, data, periodo, busca: busca || undefined, limite: 100, depois: pageParam ?? undefined,
        responsavel: soMinhas && eu ? eu.id : undefined,
      } },
    })),
    getNextPageParam: (u) => u.proximo,
  });

  const fim = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = fim.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e?.isIntersecting && consulta.hasNextPage && !consulta.isFetchingNextPage) consulta.fetchNextPage();
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [consulta]);

  const itens = useMemo(() => consulta.data?.pages.flatMap((p) => p.itens) ?? [], [consulta.data]);
  const tarefa = itens.find((t) => t.id === aberta) ?? null;
  const gestor = !!eu?.gestor;

  const concluir = useMutation({
    mutationFn: (id: string) => exigir(api.POST("/api/tarefas/{tarefa_id}/concluir", { params: { path: { tarefa_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tarefas"] }),
  });

  usePainel(useMemo(() => {
    if (criando) return <NovaTarefa aoFechar={() => setCriando(false)} />;
    if (!tarefa) return null;
    const sit = situacaoDe(tarefa);
    return (
      <PainelLateral titulo={tarefa.titulo} sobre={tarefa.processo_formatado ? <span className="numeros">{tarefa.processo_formatado}</span> : "sem processo"}
        aoFechar={() => { setAberta(null); concluir.reset(); }}
        rodape={tarefa.status === "aberta" ? (
          <Botao variante="primario" carregando={concluir.isPending} onClick={() => concluir.mutate(tarefa.id)}>
            {tarefa.necessaria_conferencia ? "Enviar para conferência" : "Concluir tarefa"}
          </Botao>
        ) : undefined}>
        <div className="flex flex-col gap-4">
          <Etiqueta tom={sit.tom}>{sit.texto}</Etiqueta>
          {concluir.error && <p role="alert" className="text-meta text-alerta">{concluir.error instanceof ErroApi ? concluir.error.message : "Não foi possível concluir."}</p>}
          <Dados itens={[
            { rotulo: "Prazo", valor: <Prazo execucao={tarefa.execucao} fatal={tarefa.fatal} urgencia={tarefa.urgencia} /> },
            { rotulo: "Cliente", valor: tarefa.cliente ?? <span className="text-texto-3">sem cliente</span> },
            { rotulo: "Responsável", valor: tarefa.responsavel.nome },
            ...(tarefa.revisor ? [{ rotulo: "Confere", valor: tarefa.revisor.nome }] : []),
            ...(tarefa.pontos != null ? [{ rotulo: "Pontos", valor: <span className="numeros">{tarefa.pontos}</span> }] : []),
          ]} />
          {tarefa.descricao && <p className="break-words whitespace-pre-line text-texto-2">{tarefa.descricao}</p>}
          {tarefa.processo && (
            <Link to="/processos/$numero" params={{ numero: tarefa.processo }} className="font-medium text-texto-1 underline underline-offset-2">
              Abrir o processo
            </Link>
          )}
        </div>
      </PainelLateral>
    );
  }, [criando, tarefa, concluir]));

  const filtro = [situacao !== "aberto" && SITUACOES.find((s) => s.valor === situacao)!.rotulo, soMinhas && "Só as minhas"].filter(Boolean).join(" · ");

  return (
    <div>
      <CabecalhoTela titulo="Tarefas" contexto="prazo fatal mais próximo primeiro"
        acoes={<Botao variante="primario" icone={<Plus size={14} aria-hidden />} onClick={() => { setAberta(null); setCriando(true); }}>Nova tarefa</Botao>} />
      <BarraFiltros busca={digitado} aoBuscar={setDigitado} placeholder="Buscar tarefa ou cliente"
        total={consulta.data ? `${itens.length}${consulta.hasNextPage ? "+" : ""} ${itens.length === 1 ? "tarefa" : "tarefas"}` : undefined}>
        <MenuFiltro rotulo="Filtro" valor={filtro || null}>
          <GrupoMenu titulo="Situação">
            {SITUACOES.map((s) => <OpcaoMenu key={s.valor} marcada={situacao === s.valor} aoEscolher={() => setSituacao(s.valor)}>{s.rotulo}</OpcaoMenu>)}
          </GrupoMenu>
          {gestor && <>
            <div className="fio-t" />
            <GrupoMenu titulo="Responsável">
              <OpcaoMenu marcada={!soMinhas} aoEscolher={() => setSoMinhas(false)}>Toda a equipe</OpcaoMenu>
              <OpcaoMenu marcada={soMinhas} aoEscolher={() => setSoMinhas(true)}>Só as minhas</OpcaoMenu>
            </GrupoMenu>
          </>}
        </MenuFiltro>
        <MenuPeriodo data={data} periodo={periodo} aoMudar={(d, p) => { setData(d); setPeriodo(p); }} />
      </BarraFiltros>

      {consulta.isPending ? <Esqueleto linhas={10} /> : consulta.isError ? (
        <Falha mensagem={consulta.error instanceof ErroApi ? consulta.error.message : "Não foi possível carregar as tarefas."} aoTentar={() => consulta.refetch()} />
      ) : itens.length === 0 ? (
        <Vazio titulo={busca ? `Nada encontrado para “${busca}”` : situacao === "aberto" && periodo === "qualquer" ? "Nenhuma tarefa em aberto" : "Nenhuma tarefa com esses filtros"}>
          {situacao === "aberto" && periodo === "qualquer" && !busca ? "Quando o controller lançar uma tarefa para você, ela aparece aqui." : "Troque a situação ou o período no filtro."}
        </Vazio>
      ) : (
        <>
          <ListaTarefas comResponsavel={gestor && !soMinhas}>
            {itens.map((t) => (
              <LinhaTarefa key={t.id} t={resumo(t)} comResponsavel={gestor && !soMinhas} ativa={aberta === t.id}
                aoAbrir={() => { setCriando(false); setAberta(t.id); }} />
            ))}
          </ListaTarefas>
          <div ref={fim} className="flex justify-center py-4">
            {consulta.hasNextPage && <Botao variante="fantasma" carregando={consulta.isFetchingNextPage} onClick={() => consulta.fetchNextPage()}>Carregar mais</Botao>}
          </div>
        </>
      )}
    </div>
  );
}

/** Nova tarefa no painel: o essencial. Lançar pela biblioteca, com prazo calculado, é a tela Lançamentos (E4-05). */
function NovaTarefa({ aoFechar }: { aoFechar: () => void }) {
  const qc = useQueryClient();
  const [titulo, setTitulo] = useState("");
  const [tipo, setTipo] = useState<"peca" | "pendencia" | "reuniao" | "inicial" | "diligencia" | "documentos" | "outro">("peca");
  const [processo, setProcesso] = useState("");
  const [execucao, setExecucao] = useState("");
  const [fatal, setFatal] = useState("");
  const criar = useMutation({
    mutationFn: () => exigir(api.POST("/api/tarefas", { body: {
      titulo, tipo, processo: processo.trim() || undefined, execucao: execucao || undefined, fatal: fatal || undefined,
      necessaria_conferencia: false,
    } })),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tarefas"] }); aoFechar(); },
  });
  function enviar(e: FormEvent) { e.preventDefault(); criar.mutate(); }
  return (
    <PainelLateral titulo="Nova tarefa" aoFechar={aoFechar}
      rodape={<><Botao onClick={aoFechar}>Cancelar</Botao><Botao variante="primario" type="submit" form="nova-tarefa" carregando={criar.isPending}>Criar tarefa</Botao></>}>
      <form id="nova-tarefa" onSubmit={enviar} className="flex flex-col gap-4">
        <Campo rotulo="O que fazer" required value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex.: Réplica à contestação" />
        <Selecao rotulo="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="peca">Peça</option>
          <option value="pendencia">Pendência (pode ficar sem processo)</option>
          <option value="reuniao">Reunião (pode ficar sem processo)</option>
          <option value="inicial">Inicial (ainda sem processo)</option>
          <option value="diligencia">Diligência</option>
          <option value="documentos">Documentos</option>
          <option value="outro">Outro</option>
        </Selecao>
        <Campo rotulo="Número do processo" value={processo} onChange={(e) => setProcesso(e.target.value)} placeholder="0000000-00.0000.0.00.0000"
          ajuda="Peça, diligência e documentos precisam de processo" />
        <div className="grid grid-cols-2 gap-3">
          <Campo rotulo="Execução" type="date" value={execucao} onChange={(e) => setExecucao(e.target.value)} />
          <Campo rotulo="Prazo fatal" type="date" value={fatal} onChange={(e) => setFatal(e.target.value)} />
        </div>
        {criar.error && <p role="alert" className="text-meta text-alerta">{criar.error instanceof ErroApi ? criar.error.message : "Não foi possível criar."}</p>}
      </form>
    </PainelLateral>
  );
}
